import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { Combobox, DropdownButton } from "@similarweb/ui-components";
import { Text, Box, Flex } from "@similarweb/ui-components/foundations";

import { SetupFieldLoader } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/SetupFieldLoader";
import { translate } from "app/filters/i18nFilter";
import { getDefaultLanguageForCountry } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/utils";
import type { ILanguage } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { allTrackers } from "app/services/track/track";

interface ILLMSetupLanguageSelectorProps {
    selectedLanguage: ILanguage;
    onLanguageChange: (language: ILanguage) => void;
    languagesList: ILanguage[];
    isLoading: boolean;
    countryCode?: string;
}

export const LLMSetupLanguageSelector: FC<ILLMSetupLanguageSelectorProps> = ({
    selectedLanguage,
    onLanguageChange,
    languagesList = [],
    isLoading,
    countryCode,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const filteredLanguages = useMemo<ILanguage[]>(() => {
        if (!searchValue) {
            return languagesList;
        }

        return languagesList.filter((lang) => lang.name.toLowerCase().includes(searchValue.toLowerCase()));
    }, [languagesList, searchValue]);

    //automatically detect the default language for the selected country
    useEffect(() => {
        if (!countryCode || !languagesList?.length) {
            return;
        }

        const defaultLangId = getDefaultLanguageForCountry(countryCode);
        if (!defaultLangId) {
            return;
        }

        const matchedLang = languagesList.find((lang) => lang.id === defaultLangId);
        if (matchedLang && matchedLang.id !== selectedLanguage.id) {
            onLanguageChange(matchedLang);
        }
    }, [countryCode, languagesList]);

    const handleLanguageSelect = (langId: string): void => {
        const language = languagesList.find((lang) => lang.id === langId);

        if (language) {
            const { name } = language;

            allTrackers.dispatchTrackEvent("Dropdown", "Select", `Ai Brand Visibility/Language/${name}`);
            onLanguageChange(language);
            setSearchValue("");
        }

        setIsOpen(false);
    };

    const onToggle = (state: boolean): void => {
        if (state) {
            allTrackers.dispatchTrackEvent("Dropdown", "Open", `Ai Brand Visibility/Language`);
        } else {
            setSearchValue("");
        }

        setIsOpen(state);
    };

    return (
        <Box
            position="relative"
            borderRadius="3px"
            width="232px"
            height="40px"
        >
            {isLoading ? (
                <SetupFieldLoader />
            ) : (
                <Combobox
                    isOpen={isOpen}
                    onOpenChange={onToggle}
                    placement="bottom-start"
                    matchTriggerWidth
                    data-automation="llm-setup-language-dropdown"
                >
                    <Combobox.Trigger>
                        <Flex width="100%">
                            <DropdownButton isOpen={isOpen}>
                                <Text
                                    fontVariant="chip1"
                                    data-automation="llm-setup-selected-language"
                                >
                                    {selectedLanguage.name}
                                </Text>
                            </DropdownButton>
                        </Flex>
                    </Combobox.Trigger>
                    <Combobox.SearchInput
                        value={searchValue}
                        onChange={setSearchValue}
                        placeholder={translate("llm.setup.language.search.placeholder")}
                        data-automation="llm-setup-language-search-input"
                    />
                    <Combobox.Content emptyState={translate("dropdown.no.results")}>
                        {filteredLanguages.map(({ id, name }) => (
                            <Combobox.Option
                                key={id}
                                id={id}
                                textValue={name}
                                onClick={() => handleLanguageSelect(id)}
                                data-automation={`llm-setup-language-item-${id}`}
                            >
                                <Combobox.OptionLabel>{name}</Combobox.OptionLabel>
                            </Combobox.Option>
                        ))}
                    </Combobox.Content>
                </Combobox>
            )}
        </Box>
    );
};
